import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Shield, User, XCircle } from 'lucide-react';
import { Badge, Card, CardTitle, PageSpinner, EmptyState } from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { useAuth } from '@/lib/auth/useAuth';
import { fetchSecurityStatus } from './api';

const ROLE_TONE: Record<string, 'success' | 'primary' | 'warning' | 'neutral'> = {
  AUTHMATECH_SUPER_ADMIN: 'success',
  AUTHMATECH_ADMIN: 'primary',
  AUTHMATECH_OPERATOR: 'primary',
  AUTHMATECH_FINANCE: 'warning',
  AUTHMATECH_SUPPORT: 'neutral',
  AUTHMATECH_VIEWER: 'neutral',
};

export function SecurityPage() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['dashboard', 'security-status'],
    queryFn: fetchSecurityStatus,
  });

  if (query.isLoading) return <PageSpinner />;

  if (query.isError || !query.data) {
    return (
      <EmptyState
        message={
          isApiError(query.error)
            ? query.error.firstErrorMessage
            : 'Could not load security status.'
        }
      />
    );
  }

  const s = query.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Security & Access</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          Authentication configuration and your current session details.
        </p>
      </div>

      {/* Critical warnings */}
      {s.securityWarning && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-50 p-4 dark:bg-amber-500/10">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Security notice</p>
            <p className="mt-0.5 text-sm text-amber-700 dark:text-amber-400">{s.securityWarning}</p>
          </div>
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatusCard
          icon={<Shield className="h-5 w-5" />}
          label="Authmatech Identity"
          enabled={s.keycloakEnabled}
          detail={s.keycloakEnabled ? 'Dashboard authentication active' : 'Dev mode — access unprotected'}
        />
        <StatusCard
          icon={<Shield className="h-5 w-5" />}
          label="API key protection"
          enabled={s.apiKeyAuthEnabled}
          detail="Customer verification endpoints"
        />
        <StatusCard
          icon={<Shield className="h-5 w-5" />}
          label="Request signing"
          enabled={s.hmacSignatureEnabled}
          detail={s.hmacSignatureEnabled ? 'HMAC signing active' : 'Disabled — enable before production'}
        />
      </div>

      {/* Session — logged-in user */}
      {user && (
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
              <User className="h-4 w-4" />
            </div>
            <CardTitle>Your session</CardTitle>
          </div>
          <dl className="space-y-3 text-sm">
            <Row label="Name" value={<span className="font-medium">{user.name}</span>} />
            <Row label="Email" value={<span className="font-mono text-xs">{user.email}</span>} />
            <Row label="Username" value={<span className="font-mono text-xs">{user.preferredUsername}</span>} />
            <Row
              label="Roles"
              value={
                <div className="flex flex-wrap justify-end gap-1">
                  {user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <Badge key={role} tone={ROLE_TONE[role] ?? 'neutral'}>
                        {role.replace('AUTHMATECH_', '')}
                      </Badge>
                    ))
                  ) : (
                    <Badge tone="neutral">No roles assigned</Badge>
                  )}
                </div>
              }
            />
            <Row
              label="Session management"
              value={<Badge tone="success">Authmatech Identity — active</Badge>}
            />
          </dl>
        </Card>
      )}

      {/* Identity provider configuration */}
      <Card>
        <CardTitle className="mb-4">Authmatech Identity configuration</CardTitle>
        <dl className="space-y-3 text-sm">
          <Row label="Environment" value={<Badge tone="primary">{s.environment}</Badge>} />
          <Row
            label="Issuer URI"
            value={
              s.keycloakIssuerUri ? (
                <code className="break-all text-xs font-mono">{s.keycloakIssuerUri}</code>
              ) : (
                <span className="italic text-fg-subtle">Not configured (dev mode)</span>
              )
            }
          />
          <Row
            label="Token endpoint"
            value={
              s.keycloakIssuerUri ? (
                <code className="break-all text-xs font-mono">
                  {s.keycloakIssuerUri}/protocol/openid-connect/token
                </code>
              ) : (
                <span className="italic text-fg-subtle">—</span>
              )
            }
          />
          <Row
            label="Dashboard authentication"
            value={
              <Badge tone={s.keycloakEnabled ? 'success' : 'warning'}>
                {s.keycloakEnabled ? 'Active' : 'Disabled'}
              </Badge>
            }
          />
          <Row
            label="Request signing"
            value={
              <Badge tone={s.hmacSignatureEnabled ? 'success' : 'neutral'}>
                {s.hmacSignatureEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            }
          />
        </dl>
      </Card>

      {/* Authentication flows */}
      <Card>
        <CardTitle className="mb-3">Authentication flows</CardTitle>
        <div className="space-y-3 text-sm">
          <FlowRow
            label="Dashboard login — secure redirect flow"
            description="React dashboard → Authmatech Identity → access token issued → backend validates token → role-based authorization enforced"
            active
          />
          <FlowRow
            label="Customer verification API — API key"
            description="X-API-KEY + X-CLIENT-ID → hash match against client record → request allowed or rejected at the gateway"
            active
          />
          <FlowRow
            label="HMAC request signing"
            description="X-SIGNATURE header validated against request body hash + client secret — adds tamper protection"
            active={s.hmacSignatureEnabled}
          />
        </div>
      </Card>

      {/* Role hierarchy */}
      <Card>
        <CardTitle className="mb-3">Role access matrix</CardTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-fg-muted">
                <th className="pb-2 pr-4 font-medium">Role</th>
                <th className="pb-2 px-2 text-center font-medium">Clients</th>
                <th className="pb-2 px-2 text-center font-medium">Onboard</th>
                <th className="pb-2 px-2 text-center font-medium">Keys</th>
                <th className="pb-2 px-2 text-center font-medium">Balance</th>
                <th className="pb-2 px-2 text-center font-medium">Verifications</th>
                <th className="pb-2 px-2 text-center font-medium">Security</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                { role: 'SUPER_ADMIN', clients: '✓', onboard: '✓', keys: '✓', balance: '✓', verifications: '✓', security: '✓' },
                { role: 'ADMIN', clients: '✓', onboard: '✓', keys: '✓', balance: '✓', verifications: '✓', security: '✓' },
                { role: 'OPERATOR', clients: 'read', onboard: '✓', keys: '—', balance: '—', verifications: '✓', security: '—' },
                { role: 'SUPPORT', clients: 'read', onboard: '—', keys: '—', balance: '—', verifications: 'read', security: '—' },
                { role: 'FINANCE', clients: 'read', onboard: '—', keys: '—', balance: '✓', verifications: '—', security: '—' },
                { role: 'VIEWER', clients: '—', onboard: '—', keys: '—', balance: '—', verifications: '—', security: '—' },
              ].map((r) => (
                <tr key={r.role} className="text-center">
                  <td className="py-2 pr-4 text-left font-mono font-medium text-fg">{r.role}</td>
                  {[r.clients, r.onboard, r.keys, r.balance, r.verifications, r.security].map(
                    (v, i) => (
                      <td key={i} className="px-2 py-2 text-fg-muted">
                        {v === '✓' ? (
                          <CheckCircle className="mx-auto h-3.5 w-3.5 text-emerald-500" />
                        ) : v === 'read' ? (
                          <span className="text-primary-500">R</span>
                        ) : (
                          <span className="text-fg-subtle">—</span>
                        )}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatusCard({ icon, label, enabled, detail }: { icon: ReactNode; label: string; enabled: boolean; detail: string }) {
  return (
    <Card>
      <div className="flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            enabled
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
              : 'bg-surface-subtle text-fg-subtle'
          }`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-0.5 text-xs text-fg-muted">{detail}</p>
        </div>
        <div className="shrink-0">
          {enabled ? (
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          ) : (
            <XCircle className="h-4 w-4 text-fg-subtle" />
          )}
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function FlowRow({ label, description, active }: { label: string; description: string; active: boolean }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border p-3">
      <div className="mt-0.5 shrink-0">
        {active ? (
          <CheckCircle className="h-4 w-4 text-emerald-500" />
        ) : (
          <XCircle className="h-4 w-4 text-fg-subtle" />
        )}
      </div>
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-xs text-fg-muted">{description}</p>
      </div>
    </div>
  );
}
